import { TransText } from '@starsoft/common/interfaces/trans-text';
import { ComponentType } from 'react';
import { TransWithDefault } from '../TransWithDefault';
import { useSettingsStore } from '@starsoft/common/hooks/useSettingsStore';
import { CommonSettings } from '@starsoft/common/interfaces';

/**
 * TransTextWithDefault Component
 * This component is responsible for rendering a translation text component with default fallback.
 * It utilizes the settings store to determine the appropriate translation component to use.
 *
 * @param {TransText} props - The properties for the component.
 * @param {object} props.components - Custom components to be used within the translation text.
 * @param {string} props.i18nKey - The internationalization key for the translation.
 * @param {object} props.values - The values to be interpolated in the translation string.
 *
 * @returns {JSX.Element} The rendered translation component or a default translation component if none is specified.
 *
 * @example
 * // Usage example:
 * <TransTextWithDefault
 *   components={{ strong: <strong />, br: <br /> }}
 *   i18nKey="example_key"
 *   values={{ name: 'John' }}
 * />
 */
export function TransTextWithDefault({ components, ...props }: TransText) {
  const { state } = useSettingsStore();
  const settings: CommonSettings = state.settings;

  // Retrieve the TransText component from settings, if available
  const TransText: ComponentType<TransText> =
    settings.translationComponents.TransText;

  // If no TransText component is found, use the default translation component
  if (!TransText) {
    return <TransWithDefault i18nKey="" {...props} />;
  }

  // Render the TransText component with provided components and props
  return (
    <TransText
      components={{
        strong: <strong />,
        br: <br />,
        p: <p />,
        div: <div />,
        span: <span />,
        ...components,
      }}
      {...props}
    />
  );
}
